import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Tabs,
  Tab,
  Menu,
  Avatar,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Close,
  Delete,
  MoreVert,
  WhatsApp,
  Send,
  NoteAdd,
  DriveFileMove,
  SwapHoriz,
  PersonAddAlt1,
  RestoreFromTrash,
  EmojiEvents,
  Cancel,
  PersonOutline,
  Schedule as ScheduleIcon,
  Settings,
  ArrowBack,
} from "@mui/icons-material";
import PipelineHub from "../components/crm/PipelineHub";
import OverdueLeadsModal from "../components/crm/OverdueLeadsModal";
import MessageMedia from "../components/crm/MessageMedia";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  crmService,
  Pipeline,
  PipelineSummary,
  LeadBoard,
  LeadBoardStage,
  LeadCard,
  LeadDetail,
  LEAD_SOURCES,
  LEAD_PRIORITIES,
  LEAD_CONTACT_ORIGINS,
  LEAD_ORIGIN_DETAIL_LABEL,
  ParentRelation,
  AssignableUser,
  LeadPatientMatch,
} from "../services/crmService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import PhoneWithCountryInput from "../components/PhoneWithCountryInput";
import { EMPTY_PHONE, leadPhoneValue, phoneNational } from "../utils/phone";

// Balões da conversa: separador por dia e só a hora dentro do balão, como no chat interno.
const isSameCalendarDay = (a: string, b: string): boolean => {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
};
const conversationDayLabel = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  if (isSameCalendarDay(iso, now.toISOString())) return "Hoje";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(iso, yesterday.toISOString())) return "Ontem";
  return d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
};
const conversationHour = (iso: string): string =>
  new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

// Funil de captação (CRM): kanban de leads. Leads inbound chegam pelo webhook do
// WhatsApp; o arrasto usa DnD nativo HTML5 e, no toque (mobile), o menu do card
// oferece "Mover para" — nada fica inacessível sem mouse.

const NAME_MAX = 200;
const PHONE_MAX = 20;
const EMAIL_MAX = 255;
const NOTE_MAX = 4000;
const LOST_REASON_MAX = 500;
const MESSAGE_MAX = 4096;

// A mensagem inbound chega pelo webhook da Meta em ~1s, mas a tela só a mostraria no
// próximo GET. Enquanto não houver SSE, o lead aberto é repescado neste intervalo.
const LEAD_POLL_MS = 8000;

/** Só o que muda quando chega mensagem/atividade nova — evita re-render a cada tick. */
const detailSignature = (lead: LeadDetail): string => {
  const last = lead.messages[lead.messages.length - 1];
  return [
    lead.messages.length,
    last?.id ?? "",
    last?.status ?? "",
    lead.activities.length,
    lead.stageId ?? "",
    lead.assignedToUserId ?? "",
  ].join("|");
};

const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1rem 1.25rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 2,
};

const filterFieldSx = {
  // altura fixa SÓ em campo de linha única: em multiline o textarea cresce e a
  // altura travada faz o texto vazar para fora da caixa (bug recorrente do projeto)
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": {
    height: "40px",
  },
  "& .MuiOutlinedInput-root": {
    fontSize: "1rem",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#ced4da", legend: { maxWidth: "100%" } },
    "&:hover fieldset": { borderColor: "#ced4da" },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
      boxShadow: "0 0 0 3px rgba(3, 180, 198, 0.1)",
    },
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    padding: "0.375rem 0.5rem",
    color: colors.textPrimary,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    color: colors.textSecondary,
    backgroundColor: "white",
    paddingLeft: "4px",
    paddingRight: "4px",
    "&.Mui-focused": { color: colors.primary },
  },
};

const PRIORITY_COLORS: Record<string, { bg: string; fg: string }> = {
  low: { bg: "#e9ecef", fg: "#495057" },
  medium: { bg: "#fff3cd", fg: "#856404" },
  high: { bg: "#f8d7da", fg: "#721c24" },
};

const normalize = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const daysInStage = (stageEnteredAt: string | null): string => {
  if (!stageEnteredAt) return "";
  const days = Math.floor((Date.now() - new Date(stageEnteredAt).getTime()) / 86400000);
  if (days <= 0) return "hoje";
  if (days === 1) return "1 dia";
  return `${days} dias`;
};

/** Nascimento é data pura: formatar sem hora evita o deslocamento de fuso na virada do dia. */
const formatDate = (value: string): string =>
  new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR");

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

interface LeadForm {
  id: string;
  name: string;
  whatsapp: string;
  phone: string;
  email: string;
  source: string;
  priority: string;
  stageId: string;
  patientName: string;
  patientBirthDate: string;
  diagnosisNote: string;
  notes: string;
  contactOrigin: string;
  contactOriginNote: string;
  attendanceRating: string;
  patientIsContact: boolean;
  contactRelationId: string;
}

const emptyLeadForm: LeadForm = {
  id: "",
  name: "",
  whatsapp: "",
  phone: "",
  email: "",
  source: "manual",
  priority: "medium",
  stageId: "",
  patientName: "",
  patientBirthDate: "",
  diagnosisNote: "",
  notes: "",
  contactOrigin: "",
  contactOriginNote: "",
  attendanceRating: "",
  patientIsContact: false,
  contactRelationId: "",
};

interface AssignOption {
  userId: string;
  name: string;
}

const CrmFunnel: React.FC = () => {
  const { navigateTo, getParam } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const theme = useTheme();
  // detalhe do lead em modal (padrão do sistema); tela cheia no celular
  const fullScreenDetail = useMediaQuery(theme.breakpoints.down("sm"));

  // escopo da entidade da sessão (nunca de parâmetro da URL)
  const entityId = user?.entityId || "";
  // configuração do funil é página administrativa própria (crm-settings)
  const canConfigure = (user?.allowedPages || []).includes("crm-settings");

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>("");
  const [board, setBoard] = useState<LeadBoard | null>(null);
  const [assignOptions, setAssignOptions] = useState<AssignOption[]>([]);
  const [parentRelations, setParentRelations] = useState<ParentRelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noPipeline, setNoPipeline] = useState(false);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  const [detail, setDetail] = useState<LeadDetail | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  // Responder assume o atendimento (RN-615). A confirmação existe porque o silêncio da IA
  // naquele lead é DEFINITIVO — consequência grande para um gesto pequeno.
  const [confirmAssumeOpen, setConfirmAssumeOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  // edição do lead acontece na aba Dados do detalhe (padrão pedido pelo Hilton)
  const [dataForm, setDataForm] = useState<LeadForm>(emptyLeadForm);

  useEffect(() => {
    if (!detail) return;
    setDataForm({
      id: detail.id,
      name: detail.name,
      whatsapp: detail.whatsapp || "",
      phone: detail.phone || "",
      email: detail.email || "",
      source: detail.source,
      priority: detail.priority,
      stageId: detail.stageId || "",
      patientName: detail.patientName || "",
      patientBirthDate: detail.patientBirthDate ? detail.patientBirthDate.substring(0, 10) : "",
      diagnosisNote: detail.diagnosisNote || "",
      notes: detail.notes || "",
      contactOrigin: detail.contactOrigin || "",
      contactOriginNote: detail.contactOriginNote || "",
      attendanceRating:
        detail.attendanceRating != null ? String(detail.attendanceRating) : "",
      patientIsContact: detail.patientIsContact,
      contactRelationId: detail.contactRelationId || "",
    });
  }, [detail]);

  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [leadForm, setLeadForm] = useState<LeadForm>(emptyLeadForm);

  const [lostDialog, setLostDialog] = useState<{ leadId: string; stageId: string } | null>(null);
  const [lostReason, setLostReason] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<LeadDetail | null>(null);

  // Recorte do quadro: excluir não pode ser invisível, mas ver os excluídos é FILTRO — herda
  // busca, prioridade e origem, e não vira uma lista à parte que estoura quando há muitos.
  const [showDeleted, setShowDeleted] = useState(false);

  // conversão em paciente: a checagem de duplicidade informa, não bloqueia
  const [convertOpen, setConvertOpen] = useState(false);
  const [matches, setMatches] = useState<LeadPatientMatch[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [intakeLink, setIntakeLink] = useState<string | null>(null);
  const [convertForm, setConvertForm] = useState({
    patientName: "",
    birthDate: "",
    phone: EMPTY_PHONE,
    email: "",
    responsibleName: "",
  });

  // transferência para outro pipeline: destino, estágio e responsável são obrigatórios
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferPipelineId, setTransferPipelineId] = useState("");
  const [transferStageId, setTransferStageId] = useState("");
  const [transferUserId, setTransferUserId] = useState("");
  const [transferUsers, setTransferUsers] = useState<AssignableUser[]>([]);
  const [transferLoading, setTransferLoading] = useState(false);

  const [dragLeadId, setDragLeadId] = useState<string | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [cardMenu, setCardMenu] = useState<{ anchor: HTMLElement; lead: LeadCard } | null>(null);

  // Painel de pipelines (hub) x quadro: sem pipeline escolhido, mostra o painel.
  // O estado é local porque navigateTo para a MESMA página não re-renderiza (currentPage
  // não muda) e ainda preserva os params antigos — o que travava a abertura do quadro e o
  // voltar. A URL é mantida em sincronia à mão para o link seguir compartilhável.
  const [routePipelineId, setRoutePipelineId] = useState<string | null>(() => getParam("pipelineId"));

  const syncUrl = useCallback((pipelineId: string | null) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", "crm-funnel");
    if (pipelineId) {
      params.set("pipelineId", pipelineId);
    } else {
      params.delete("pipelineId");
    }
    window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);
  }, []);

  const openPipeline = useCallback(
    (pipelineId: string) => {
      setRoutePipelineId(pipelineId);
      syncUrl(pipelineId);
    },
    [syncUrl],
  );

  const backToHub = useCallback(() => {
    setRoutePipelineId(null);
    setBoard(null);
    syncUrl(null);
  }, [syncUrl]);

  // botão voltar do navegador acompanha a troca painel/quadro
  useEffect(() => {
    const onPop = () => setRoutePipelineId(new URLSearchParams(window.location.search).get("pipelineId"));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [summaries, setSummaries] = useState<PipelineSummary[]>([]);
  const [summariesLoading, setSummariesLoading] = useState(true);

  useEffect(() => {
    if (routePipelineId || !entityId) return;
    setSummariesLoading(true);
    crmService
      .getPipelineSummaries(entityId)
      .then(setSummaries)
      .catch(() => setSummaries([]))
      .finally(() => setSummariesLoading(false));
  }, [routePipelineId, entityId]);

  const loadBoard = useCallback(
    async (pipelineId?: string | null, deleted = false) => {
      if (!entityId) {
        setError("Sessão sem entidade definida. Refaça o login para continuar.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setNoPipeline(false);
      try {
        const pipelineList = await crmService.getPipelines(entityId, false);
        setPipelines(pipelineList);
        if (pipelineList.length === 0) {
          setNoPipeline(true);
          setBoard(null);
          return;
        }
        const targetPipeline =
          pipelineId ||
          selectedPipelineId ||
          (pipelineList.find((p) => p.isDefault) || pipelineList[0]).id;
        // responsáveis elegíveis são os do funil (membros); sem membros, todos da entidade
        const [boardData, assignable] = await Promise.all([
          crmService.getBoard(entityId, targetPipeline, deleted),
          crmService.getAssignableUsers(entityId, targetPipeline).catch(() => []),
        ]);
        setBoard(boardData);
        setSelectedPipelineId(boardData.pipelineId);
        setAssignOptions(assignable);
      } catch {
        setError("Não foi possível carregar o funil. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    // selectedPipelineId fica fora de propósito: o board recarrega pelo id explícito
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityId],
  );

  useEffect(() => {
    // o quadro só carrega quando há pipeline na URL (fora isso, quem manda é o painel)
    if (!routePipelineId) {
      setLoading(false);
      return;
    }
    loadBoard(routePipelineId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBoard, routePipelineId]);

  useEffect(() => {
    if (!entityId) return;
    crmService.getParentRelations(entityId).then(setParentRelations).catch(() => setParentRelations([]));
  }, [entityId]);

  const hasFilters = !!(search || priorityFilter || sourceFilter);

  const matchesFilters = useCallback(
    (lead: LeadCard): boolean => {
      if (priorityFilter && lead.priority !== priorityFilter) return false;
      if (sourceFilter && lead.source !== sourceFilter) return false;
      if (search) {
        const term = normalize(search);
        const haystack = normalize(
          `${lead.name} ${lead.whatsappName || ""} ${lead.patientName || ""} ${lead.whatsapp || ""} ${lead.phone || ""}`,
        );
        if (!haystack.includes(term)) return false;
      }
      return true;
    },
    [search, priorityFilter, sourceFilter],
  );

  const visibleStages: LeadBoardStage[] = useMemo(() => {
    if (!board) return [];
    return board.stages.map((stage) => ({
      ...stage,
      leads: stage.leads.filter(matchesFilters),
    }));
  }, [board, matchesFilters]);

  const openLead = useCallback(
    async (leadId: string) => {
      setDrawerOpen(true);
      setDetailLoading(true);
      setDrawerTab(0);
      setReply("");
      setNote("");
      try {
        const data = await crmService.getLead(leadId, entityId);
        setDetail(data);
      } catch {
        showToast("Não foi possível abrir o lead.", "error");
        setDrawerOpen(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [entityId, showToast],
  );

  const refreshDetail = useCallback(async () => {
    if (!detail) return;
    try {
      setDetail(await crmService.getLead(detail.id, entityId));
    } catch {
      // detalhe é recarregável pela próxima ação; falha aqui não derruba o board
    }
  }, [detail, entityId]);

  const detailId = detail?.id ?? null;

  useEffect(() => {
    if (!drawerOpen || !detailId) return;

    const pull = async () => {
      // aba em segundo plano não consome API; ao voltar, o visibilitychange busca na hora
      if (document.hidden) return;
      try {
        const fresh = await crmService.getLead(detailId, entityId);
        setDetail((current) => {
          // resposta de um lead que já não está aberto (troca durante o request) é descartada
          if (!current || current.id !== fresh.id) return current;
          return detailSignature(current) === detailSignature(fresh) ? current : fresh;
        });
      } catch {
        // silencioso por natureza: polling não gera toast nem fecha o drawer
      }
    };

    const timerId = setInterval(pull, LEAD_POLL_MS);
    document.addEventListener("visibilitychange", pull);
    return () => {
      clearInterval(timerId);
      document.removeEventListener("visibilitychange", pull);
    };
  }, [drawerOpen, detailId, entityId]);

  const conversationRef = useRef<HTMLDivElement | null>(null);
  const messageCount = detail?.messages.length ?? 0;

  useEffect(() => {
    // sem isto a mensagem nova cai fora da área visível e o polling não é percebido
    if (drawerTab !== 0) return;
    const node = conversationRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [drawerTab, messageCount, detailId, detailLoading]);

  const stageById = useMemo(() => {
    const map = new Map<string, LeadBoardStage>();
    board?.stages.forEach((stage) => map.set(stage.id, stage));
    return map;
  }, [board]);

  const executeMove = useCallback(
    async (leadId: string, stageId: string, lostReasonValue?: string) => {
      setSaving(true);
      try {
        await crmService.moveLead(leadId, entityId, stageId, 0, lostReasonValue || null);
        await loadBoard(selectedPipelineId, showDeleted);
        if (detail?.id === leadId) {
          await refreshDetail();
        }
      } catch (err: any) {
        const description = err?.response?.data?.description;
        showToast(description || "Não foi possível mover o lead.", "error");
      } finally {
        setSaving(false);
      }
    },
    [entityId, selectedPipelineId, detail, loadBoard, refreshDetail, showToast, showDeleted],
  );

  const requestMove = useCallback(
    (leadId: string, stageId: string) => {
      const stage = stageById.get(stageId);
      if (!stage) return;
      if (stage.isLost) {
        setLostReason("");
        setLostDialog({ leadId, stageId });
        return;
      }
      executeMove(leadId, stageId);
    },
    [stageById, executeMove],
  );

  const handleDrop = (stageId: string) => {
    setDragOverStageId(null);
    if (!dragLeadId) return;
    const leadId = dragLeadId;
    setDragLeadId(null);
    requestMove(leadId, stageId);
  };

  const openCreateLead = () => {
    const firstStage = board?.stages.find((stage) => !stage.isWon && !stage.isLost);
    setLeadForm({ ...emptyLeadForm, stageId: firstStage?.id || "" });
    setLeadDialogOpen(true);
  };

  const saveDataForm = async () => {
    if (!detail) return;
    if (!dataForm.name.trim()) {
      showToast("Informe o nome do contato.", "error");
      return;
    }
    setSaving(true);
    try {
      await crmService.updateLead(detail.id, entityId, {
        name: dataForm.name.trim(),
        phone: dataForm.phone.trim() || null,
        whatsapp: dataForm.whatsapp.trim() || null,
        email: dataForm.email.trim() || null,
        source: dataForm.source,
        priority: dataForm.priority,
        patientName: dataForm.patientName.trim() || null,
        patientBirthDate: dataForm.patientBirthDate || null,
        diagnosisNote: dataForm.diagnosisNote.trim() || null,
        notes: dataForm.notes.trim() || null,
        contactOrigin: dataForm.contactOrigin || null,
        contactOriginNote: dataForm.contactOriginNote.trim() || null,
        attendanceRating:
          dataForm.attendanceRating === "" ? null : Number(dataForm.attendanceRating),
        patientIsContact: dataForm.patientIsContact,
        contactRelationId: dataForm.patientIsContact ? null : dataForm.contactRelationId || null,
      });
      showToast("Lead atualizado.", "success");
      await refreshDetail();
      await loadBoard(selectedPipelineId, showDeleted);
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível salvar o lead.", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveLead = async () => {
    if (!leadForm.name.trim()) {
      showToast("Informe o nome do contato.", "error");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: leadForm.name.trim(),
        phone: leadForm.phone.trim() || null,
        whatsapp: leadForm.whatsapp.trim() || null,
        email: leadForm.email.trim() || null,
        source: leadForm.source,
        priority: leadForm.priority,
        patientName: leadForm.patientName.trim() || null,
        patientBirthDate: leadForm.patientBirthDate || null,
        diagnosisNote: leadForm.diagnosisNote.trim() || null,
        notes: leadForm.notes.trim() || null,
      };
      if (leadForm.id) {
        await crmService.updateLead(leadForm.id, entityId, payload);
        showToast("Lead atualizado.", "success");
      } else {
        await crmService.createLead({
          entityId,
          pipelineId: board?.pipelineId || null,
          stageId: leadForm.stageId || null,
          assignedToUserId: null,
          ...payload,
        });
        showToast("Lead criado.", "success");
      }
      setLeadDialogOpen(false);
      await loadBoard(selectedPipelineId, showDeleted);
      if (leadForm.id && detail?.id === leadForm.id) {
        await refreshDetail();
      }
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível salvar o lead.", "error");
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!detail || !reply.trim()) return;
    // Sem responsável, a IA é quem responde este lead: assumir precisa ser consciente.
    if (!detail.assignedToUserId) {
      setConfirmAssumeOpen(true);
      return;
    }
    await deliverReply();
  };

  const assumeAndSend = async () => {
    if (!detail || !user?.userId) return;
    setConfirmAssumeOpen(false);
    setSaving(true);
    try {
      await crmService.assignLead(detail.id, entityId, user.userId);
      await deliverReply({ keepSaving: true });
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível assumir o atendimento.", "error");
      setSaving(false);
    }
  };

  const deliverReply = async (options?: { keepSaving?: boolean }) => {
    if (!detail || !reply.trim()) return;
    setSaving(true);
    try {
      await crmService.sendMessage(detail.id, entityId, reply.trim());
      setReply("");
      await refreshDetail();
      await loadBoard(selectedPipelineId, showDeleted);
      showToast("Mensagem enviada.", "success");
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível enviar a mensagem.", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    if (!detail || !note.trim()) return;
    setSaving(true);
    try {
      await crmService.addNote(detail.id, entityId, note.trim());
      setNote("");
      await refreshDetail();
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível salvar a anotação.", "error");
    } finally {
      setSaving(false);
    }
  };

  const assign = async (userId: string | null) => {
    if (!detail) return;
    setSaving(true);
    try {
      await crmService.assignLead(detail.id, entityId, userId);
      await refreshDetail();
      await loadBoard(selectedPipelineId, showDeleted);
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível atribuir o lead.", "error");
    } finally {
      setSaving(false);
    }
  };

  const restore = async (leadId: string) => {
    setSaving(true);
    try {
      await crmService.restoreLead(leadId, entityId);
      showToast("Lead restaurado.", "success");
      await loadBoard(selectedPipelineId, showDeleted);
    } catch (err: any) {
      showToast(err?.response?.data?.description || "Não foi possível restaurar o lead.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Trocar o recorte recarrega o quadro: os excluídos vêm do servidor, não de filtro em memória.
  useEffect(() => {
    if (routePipelineId) {
      loadBoard(selectedPipelineId, showDeleted);
    }
    // selectedPipelineId fica fora: quem manda o recarregamento aqui é a troca de recorte
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const openConvert = async () => {
    if (!detail) return;
    setIntakeLink(null);
    setMatches([]);
    // Pré-preenchido com o que a conversa já apurou; a recepção completa o que falta.
    setConvertForm({
      patientName: (detail.patientIsContact ? detail.name : detail.patientName) || "",
      birthDate: detail.patientBirthDate ? detail.patientBirthDate.slice(0, 10) : "",
      // WhatsApp e telefone do lead são E.164 sem "+" (DDI nos dígitos); telefone curto é texto antigo, brasileiro.
      phone: leadPhoneValue(detail.whatsapp, detail.phone),
      email: detail.email || "",
      responsibleName: detail.patientIsContact ? "" : detail.name || "",
    });
    setConvertOpen(true);
    setMatchesLoading(true);
    try {
      setMatches(await crmService.getPatientMatches(detail.id, entityId));
    } catch {
      // falhar a busca não pode impedir o cadastro: ela informa, não decide
      setMatches([]);
    } finally {
      setMatchesLoading(false);
    }
  };

  const convert = async (patientId?: string | null) => {
    if (!detail) return;

    // Vincular a um cadastro existente não exige os campos: eles são do cadastro NOVO.
    if (!patientId) {
      const faltando = [
        !convertForm.patientName.trim() && "nome do paciente",
        !convertForm.birthDate && "data de nascimento",
        !phoneNational(convertForm.phone) && "telefone",
        !convertForm.email.trim() && "email",
      ].filter(Boolean);
      if (faltando.length > 0) {
        showToast(`Complete antes de cadastrar: ${faltando.join(", ")}.`, "error");
        return;
      }
    }

    setSaving(true);
    try {
      const result = await crmService.convertLead(detail.id, entityId, {
        patientId,
        ...(patientId
          ? {}
          : {
              patientName: convertForm.patientName.trim(),
              birthDate: convertForm.birthDate,
              phone: phoneNational(convertForm.phone),
              phoneDdi: convertForm.phone.ddi,
              email: convertForm.email.trim(),
              responsibleName: convertForm.responsibleName.trim() || null,
            }),
      });
      setIntakeLink(result.intakeLink);
      showToast(result.created ? "Paciente cadastrado." : "Lead vinculado ao paciente.", "success");
      await refreshDetail();
      await loadBoard(selectedPipelineId, showDeleted);
      if (!result.intakeLink) {
        setConvertOpen(false);
      }
    } catch (err: any) {
      showToast(err?.response?.data?.description || "Não foi possível concluir o cadastro.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openTransfer = () => {
    setTransferPipelineId("");
    setTransferStageId("");
    setTransferUserId("");
    setTransferUsers([]);
    setTransferOpen(true);
  };

  // Trocar o pipeline zera estágio e responsável: os dois pertencem ao destino, não ao lead.
  const chooseTransferPipeline = async (pipelineId: string) => {
    setTransferPipelineId(pipelineId);
    setTransferStageId("");
    setTransferUserId("");
    setTransferUsers([]);
    if (!pipelineId) return;

    const stages = (pipelines.find((p) => p.id === pipelineId)?.stages || [])
      .filter((stage) => stage.active && !stage.isWon && !stage.isLost);
    setTransferStageId(stages[0]?.id || "");

    setTransferLoading(true);
    try {
      const users = await crmService.getAssignableUsers(entityId, pipelineId);
      setTransferUsers(users);
    } catch {
      setTransferUsers([]);
    } finally {
      setTransferLoading(false);
    }
  };

  const submitTransfer = async () => {
    if (!detail || !transferPipelineId || !transferStageId || !transferUserId) return;
    setSaving(true);
    try {
      await crmService.transferLead(
        detail.id, entityId, transferPipelineId, transferStageId, transferUserId);
      setTransferOpen(false);
      setDrawerOpen(false);
      showToast("Lead transferido.", "success");
      await loadBoard(selectedPipelineId, showDeleted);
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível transferir o lead.", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async () => {
    if (!confirmDelete) return;
    const lead = confirmDelete;
    setConfirmDelete(null);
    setSaving(true);
    try {
      await crmService.deleteLead(lead.id, entityId);
      showToast("Lead excluído.", "success");
      setDrawerOpen(false);
      setDetail(null);
      await loadBoard(selectedPipelineId, showDeleted);
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível excluir o lead.", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderCard = (lead: LeadCard) => (
    <Paper
      key={lead.id}
      elevation={0}
      draggable={!showDeleted}
      onDragStart={(event) => {
        setDragLeadId(lead.id);
        event.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={() => {
        setDragLeadId(null);
        setDragOverStageId(null);
      }}
      onClick={() => openLead(lead.id)}
      sx={{
        p: 1.25,
        borderRadius: "10px",
        // Tracejado e esmaecido: o card está fora de circulação, e arrastar não faz sentido —
        // mover exigiria antes restaurar.
        border: showDeleted ? "1px dashed #ced4da" : "1px solid #e9ecef",
        backgroundColor: showDeleted ? "#f8f9fa" : "white",
        cursor: showDeleted ? "pointer" : "grab",
        opacity: dragLeadId === lead.id ? 0.5 : showDeleted ? 0.75 : 1,
        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.10)" },
      }}
    >
      {showDeleted && (
        <Typography
          sx={{
            fontSize: typography.fontSize.xs,
            color: colors.textSecondary,
            mb: 0.5,
            fontStyle: "italic",
          }}
        >
          Excluído
          {lead.deletedAt ? ` em ${formatDateTime(lead.deletedAt)}` : ""}
          {lead.deletedByName ? ` por ${lead.deletedByName}` : ""}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5 }}>
        <Typography
          sx={{
            fontWeight: typography.fontWeight.semibold,
            fontSize: typography.fontSize.sm,
            color: colors.textPrimary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {lead.name}
        </Typography>
        <IconButton
          size="small"
          aria-label="Ações do lead"
          onClick={(event) => {
            event.stopPropagation();
            setCardMenu({ anchor: event.currentTarget, lead });
          }}
          sx={{ mt: "-4px", mr: "-4px" }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Box>

      {lead.patientName && (
        <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, mt: 0.25 }}>
          Paciente: {lead.patientName}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: 0.75 }}>
        <Chip
          size="small"
          label={LEAD_PRIORITIES[lead.priority] || lead.priority}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            backgroundColor: PRIORITY_COLORS[lead.priority]?.bg || "#e9ecef",
            color: PRIORITY_COLORS[lead.priority]?.fg || colors.textPrimary,
          }}
        />
        {lead.source === "whatsapp" && (
          <WhatsApp sx={{ fontSize: 16, color: colors.whatsapp }} />
        )}
        {lead.stageEnteredAt && (
          <Tooltip title="Tempo neste estágio">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <ScheduleIcon sx={{ fontSize: 14, color: colors.textMuted }} />
              <Typography sx={{ fontSize: "0.7rem", color: colors.textMuted }}>
                {daysInStage(lead.stageEnteredAt)}
              </Typography>
            </Box>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {lead.assignedToName && (
          <Tooltip title={`Responsável: ${lead.assignedToName}`}>
            <Avatar
              sx={{
                width: 22,
                height: 22,
                fontSize: "0.65rem",
                backgroundColor: colors.primary,
              }}
            >
              {initials(lead.assignedToName)}
            </Avatar>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );

  const renderColumn = (stage: LeadBoardStage) => (
    <Box
      key={stage.id}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOverStageId(stage.id);
      }}
      onDragLeave={() => setDragOverStageId((current) => (current === stage.id ? null : current))}
      onDrop={() => handleDrop(stage.id)}
      sx={{
        minWidth: { xs: 260, sm: 280 },
        maxWidth: 300,
        flex: "0 0 auto",
        display: "flex",
        flexDirection: "column",
        backgroundColor: dragOverStageId === stage.id ? "#e6f7f9" : "#f1f3f5",
        borderRadius: "12px",
        border: `1px solid ${dragOverStageId === stage.id ? colors.primary : "#e9ecef"}`,
        // Coluna alta o bastante para ver vários cartões sem rolar dentro dela; em janela baixa, a página rola.
        minHeight: { xs: 480, md: "max(560px, calc(100vh - 300px))" },
        maxHeight: { xs: 640, md: "max(560px, calc(100vh - 260px))" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, p: 1.25, pb: 0.75 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: stage.color || colors.primary,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontWeight: typography.fontWeight.semibold,
            fontSize: typography.fontSize.sm,
            color: colors.textPrimary,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {stage.name}
        </Typography>
        {stage.isWon && <EmojiEvents sx={{ fontSize: 16, color: colors.success }} />}
        {stage.isLost && <Cancel sx={{ fontSize: 16, color: colors.error }} />}
        <Chip
          size="small"
          label={stage.leads.length}
          sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "white" }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          p: 1.25,
          pt: 0.5,
          overflowY: "auto",
        }}
      >
        {stage.leads.length === 0 ? (
          <Typography
            sx={{
              fontSize: typography.fontSize.xs,
              color: colors.textMuted,
              textAlign: "center",
              py: 2,
            }}
          >
            {hasFilters ? "Nenhum lead com os filtros atuais" : "Sem leads neste estágio"}
          </Typography>
        ) : (
          stage.leads.map(renderCard)
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                {routePipelineId ? board?.pipelineName || "Pipeline" : "Pipeline"}
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                {routePipelineId
                  ? "Arraste o card para mover; no celular, use o menu do card."
                  : "Captação de pacientes: escolha um pipeline para trabalhar. Os alertas mostram o que precisa de ação hoje."}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          {!routePipelineId ? (
            <div className="c4u-panel">
              {canConfigure && (
                <div className="c4u-filters__row">
                  <div className="c4u-filters__fields" />
                  <div className="c4u-filters__actions">
                    {canConfigure && (
                      <Tooltip title="Configurar pipelines (estágios, responsáveis e canais)">
                        <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => navigateTo("crm-settings")} aria-label="Configurar pipelines">
                          <Settings fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
              <PipelineHub
                summaries={summaries}
                loading={summariesLoading}
                onOpen={openPipeline}
              />
            </div>
          ) : (
          <>
          {/* Filtros reativos (padrão do sistema — sem botão Aplicar) */}
          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                {pipelines.length > 1 && (
                  <TextField
                    select
                    size="small"
                    label="Pipeline"
                    value={selectedPipelineId}
                    onChange={(e) => {
                      setSelectedPipelineId(e.target.value);
                      loadBoard(e.target.value);
                    }}
                    fullWidth
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                  >
                    {pipelines.map((pipeline) => (
                      <MenuItem key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                        {pipeline.isDefault ? " (padrão)" : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  size="small"
                  label="Busca"
                  placeholder="Nome, paciente ou telefone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: NAME_MAX }}
                />
                <TextField
                  select
                  size="small"
                  label="Prioridade"
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {Object.entries(LEAD_PRIORITIES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Origem"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {Object.entries(LEAD_SOURCES).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={showDeleted ? "deleted" : "active"}
                  onChange={(e) => setShowDeleted(e.target.value === "deleted")}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="active">Em circulação</MenuItem>
                  <MenuItem value="deleted">Excluídos</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton
                  disabled={!hasFilters}
                  onClick={() => {
                    setSearch("");
                    setPriorityFilter("");
                    setSourceFilter("");
                    setShowDeleted(false);
                  }}
                />
                <Tooltip title="Voltar para os pipelines">
                  <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={backToHub} aria-label="Voltar para os pipelines">
                    <ArrowBack fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canConfigure && (
                  <Tooltip title="Configurar pipelines (estágios, responsáveis e canais)">
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => navigateTo("crm-settings")} aria-label="Configurar pipelines">
                      <Settings fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <AddButton onClick={openCreateLead} title="Novo lead" />
              </div>
            </div>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary }} />
            </Box>
          ) : error ? (
            <Paper elevation={0} sx={{ ...filterCardSx, textAlign: "center", color: colors.error }}>
              {error}
            </Paper>
          ) : noPipeline ? (
            <Paper elevation={0} sx={{ ...filterCardSx, textAlign: "center", py: 5 }}>
              <Typography sx={{ fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
                A entidade ainda não tem pipeline de captação
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                O pipeline padrão do produto é criado na implantação do CRM. Fale com o suporte
                para habilitar a captação nesta entidade.
              </Typography>
            </Paper>
          ) : (
            <>
              {board && board.unstagedLeads.length > 0 && (
                <Paper elevation={0} sx={{ ...filterCardSx, backgroundColor: "#fff3cd", border: "1px solid #ffe69c" }}>
                  <Typography sx={{ fontSize: typography.fontSize.sm, color: "#856404" }}>
                    {board.unstagedLeads.length} lead(s) sem estágio (criados antes do funil).
                    Abra pelo nome e mova para uma coluna:{" "}
                    {board.unstagedLeads.map((lead) => (
                      <Button
                        key={lead.id}
                        size="small"
                        onClick={() => openLead(lead.id)}
                        sx={{ textTransform: "none", color: colors.primary, minWidth: 0, p: "0 4px" }}
                      >
                        {lead.name}
                      </Button>
                    ))}
                  </Typography>
                </Paper>
              )}
              {/* board com scroll horizontal próprio — a página nunca estoura no mobile */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  overflowX: "auto",
                  pb: 2,
                  scrollSnapType: { xs: "x mandatory", sm: "none" },
                  "& > *": { scrollSnapAlign: { xs: "start", sm: "none" } },
                }}
              >
                {visibleStages.map(renderColumn)}
              </Box>
            </>
          )}
          </div>
          </>
          )}
        </Container>
      </Box>

      {/* Menu do card: abrir + mover (alternativa ao arrasto — obrigatória no mobile) */}
      <Menu
        anchorEl={cardMenu?.anchor || null}
        open={!!cardMenu}
        onClose={() => setCardMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (cardMenu) openLead(cardMenu.lead.id);
            setCardMenu(null);
          }}
        >
          <ListItemIcon>
            <PersonOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Abrir lead</ListItemText>
        </MenuItem>
        {showDeleted && (
          <MenuItem
            onClick={() => {
              if (cardMenu) restore(cardMenu.lead.id);
              setCardMenu(null);
            }}
          >
            <ListItemIcon>
              <RestoreFromTrash fontSize="small" />
            </ListItemIcon>
            <ListItemText>Restaurar lead</ListItemText>
          </MenuItem>
        )}
        {!showDeleted && board?.stages
          .filter((stage) => stage.id !== cardMenu?.lead.stageId)
          .map((stage) => (
            <MenuItem
              key={stage.id}
              onClick={() => {
                if (cardMenu) requestMove(cardMenu.lead.id, stage.id);
                setCardMenu(null);
              }}
            >
              <ListItemIcon>
                <DriveFileMove fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mover para {stage.name}</ListItemText>
            </MenuItem>
          ))}
      </Menu>

      {/* Detalhe do lead — modal padrão do sistema (tela cheia no celular) */}
      <Dialog
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDetail}
        PaperProps={{
          sx: {
            borderRadius: fullScreenDetail ? 0 : "12px",
            height: fullScreenDetail ? "100%" : "85vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {detailLoading || !detail ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <CircularProgress size={32} sx={{ color: colors.primary }} />
          </Box>
        ) : (
          <>
            <DialogTitle
              sx={{
                backgroundColor: colors.primary,
                color: colors.white,
                padding: "1rem 1.25rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                <Typography
                  component="h3"
                  sx={{
                    fontSize: "1.15rem",
                    fontWeight: typography.fontWeight.semibold,
                    margin: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {detail.name}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.85)" }}>
                  {LEAD_SOURCES[detail.source] || detail.source} ·{" "}
                  {LEAD_PRIORITIES[detail.priority] || detail.priority}
                  {detail.wonAt && " · GANHO"}
                  {detail.lostAt && " · PERDIDO"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                {(user?.allowedPages || []).includes("patient-register") && (
                  <Tooltip
                    title={
                      detail.convertedPatientId
                        ? "Este lead já virou paciente"
                        : "Gerar cadastro de paciente a partir do lead"
                    }
                  >
                    <Box component="span">
                      <IconButton
                        onClick={openConvert}
                        disabled={!!detail.convertedPatientId}
                        sx={{
                          color: colors.white,
                          padding: "0.25rem",
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                          "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
                        }}
                      >
                        <PersonAddAlt1 />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                <Tooltip title="Transferir para outro pipeline">
                  <IconButton
                    onClick={openTransfer}
                    sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                  >
                    <SwapHoriz />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Excluir lead">
                  <IconButton
                    onClick={() => setConfirmDelete(detail)}
                    sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={() => setDrawerOpen(false)}
                  sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                >
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <Box sx={{ px: 1.5, pt: 2.5, pb: 1.5, borderBottom: "1px solid #e9ecef" }}>
              <TextField
                select
                size="small"
                label="Responsável"
                value={detail.assignedToUserId || ""}
                onChange={(e) => assign(e.target.value || null)}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
                helperText={
                  detail.assignedToUserId
                    ? "O atendimento é humano a partir daqui. Transferir para outra pessoa é possível; o agente de IA não volta a responder este lead."
                    : "Definir um responsável assume o atendimento — o agente de IA para de responder este lead e não volta."
                }
              >
                <MenuItem value="">Sem responsável</MenuItem>
                {assignOptions.map((option) => (
                  <MenuItem key={option.userId} value={option.userId}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Tabs
              value={drawerTab}
              onChange={(_, value) => setDrawerTab(value)}
              variant="fullWidth"
              sx={{
                borderBottom: "1px solid #e9ecef",
                "& .MuiTab-root": { textTransform: "none", minHeight: 44 },
                "& .Mui-selected": { color: `${colors.primary} !important` },
                "& .MuiTabs-indicator": { backgroundColor: colors.primary },
              }}
            >
              <Tab label="Conversa" />
              <Tab label="Linha do tempo" />
              <Tab label="Dados" />
            </Tabs>

            <Box
              ref={conversationRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                // Conversa no mesmo padrão do chat interno (RN-551): fundo pontilhado e balões
                backgroundColor: drawerTab === 0 ? "#eef1f4" : colors.background,
                backgroundImage: drawerTab === 0 ? "radial-gradient(rgba(3,180,198,0.08) 1px, transparent 1px)" : "none",
                backgroundSize: "18px 18px",
              }}
            >
              {drawerTab === 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {detail.messages.length === 0 ? (
                    <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textMuted, textAlign: "center", py: 3 }}>
                      Sem mensagens de WhatsApp com este lead.
                    </Typography>
                  ) : (
                    detail.messages.map((message, index) => {
                      const mine = message.direction === "outbound";
                      const prev = detail.messages[index - 1];
                      const newDay = !prev || !isSameCalendarDay(prev.messageTimestamp, message.messageTimestamp);
                      const sameAuthorAsPrev = !!prev && prev.direction === message.direction && !newDay;
                      return (
                        <React.Fragment key={message.id}>
                          {newDay && (
                            <Box sx={{ alignSelf: "center", my: 1, px: 1.25, py: 0.3, borderRadius: 10, backgroundColor: "rgba(255,255,255,0.9)", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
                              <Typography sx={{ fontSize: "0.7rem", color: colors.textSecondary, fontWeight: 600 }}>{conversationDayLabel(message.messageTimestamp)}</Typography>
                            </Box>
                          )}
                          <Box
                            sx={{
                              alignSelf: mine ? "flex-end" : "flex-start",
                              maxWidth: "80%",
                              mt: sameAuthorAsPrev ? 0 : 0.5,
                              px: 1.5,
                              py: 0.85,
                              backgroundColor: mine ? colors.primary : "white",
                              color: mine ? "white" : colors.textPrimary,
                              borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                              boxShadow: "0 1px 1.5px rgba(0,0,0,0.08)",
                              fontSize: "0.9rem",
                              lineHeight: 1.45,
                              wordBreak: "break-word",
                            }}
                          >
                            {message.messageType !== "text" && (
                              <MessageMedia message={message} entityId={entityId} />
                            )}
                            {(message.body || message.caption) && (
                              <Typography sx={{ fontSize: typography.fontSize.sm, whiteSpace: "pre-wrap", color: "inherit" }}>
                                {message.body || message.caption}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: "0.66rem", textAlign: "right", mt: 0.25, opacity: 0.85, color: mine ? "white" : colors.textMuted }}>
                              {conversationHour(message.messageTimestamp)}
                              {mine && message.status === "failed" && (
                                <Box component="span" sx={{ color: "#ffd0d0", fontWeight: 600 }}>
                                  {" "}
                                  · falhou
                                </Box>
                              )}
                            </Typography>
                          </Box>
                        </React.Fragment>
                      );
                    })
                  )}
                </Box>
              )}

              {drawerTab === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {detail.activities.map((activity) => (
                    <Box key={activity.id} sx={{ display: "flex", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.primary,
                          mt: "6px",
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textPrimary, whiteSpace: "pre-wrap" }}>
                          {activity.description}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: colors.textMuted }}>
                          {formatDateTime(activity.occurredAt)}
                          {activity.userName ? ` · ${activity.userName}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Nova anotação"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      fullWidth
                      sx={filterFieldSx}
                      inputProps={{ maxLength: NOTE_MAX }}
                    />
                    <Tooltip title="Salvar anotação">
                      <span>
                        <IconButton
                          onClick={saveNote}
                          disabled={!note.trim() || saving}
                          sx={{ backgroundColor: colors.primary, color: "white", "&:hover": { backgroundColor: colors.primaryHover } }}
                        >
                          <NoteAdd fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {drawerTab === 2 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Nome do contato"
                    value={dataForm.name}
                    onChange={(e) => setDataForm({ ...dataForm, name: e.target.value })}
                    fullWidth
                    size="small"
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: NAME_MAX }}
                  />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <TextField
                      label="WhatsApp (com DDD)"
                      value={dataForm.whatsapp}
                      onChange={(e) => setDataForm({ ...dataForm, whatsapp: e.target.value })}
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: PHONE_MAX }}
                      helperText={detail.whatsappName ? `Perfil no WhatsApp: ${detail.whatsappName}` : undefined}
                    />
                    <TextField
                      label="Telefone"
                      value={dataForm.phone}
                      onChange={(e) => setDataForm({ ...dataForm, phone: e.target.value })}
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: PHONE_MAX }}
                    />
                  </Box>
                  <TextField
                    label="Email"
                    value={dataForm.email}
                    onChange={(e) => setDataForm({ ...dataForm, email: e.target.value })}
                    fullWidth
                    size="small"
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: EMAIL_MAX }}
                  />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <TextField
                      select
                      label="Origem"
                      value={dataForm.source}
                      onChange={(e) => setDataForm({ ...dataForm, source: e.target.value })}
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      {Object.entries(LEAD_SOURCES).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Prioridade"
                      value={dataForm.priority}
                      onChange={(e) => setDataForm({ ...dataForm, priority: e.target.value })}
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      {Object.entries(LEAD_PRIORITIES).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  {/* Nem todo atendimento é de criança: muita gente procura a clínica para si
                      mesma. Marcar aqui copia o nome do contato e dispensa o parentesco. */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={dataForm.patientIsContact}
                        onChange={(event) =>
                          setDataForm({
                            ...dataForm,
                            patientIsContact: event.target.checked,
                            patientName: event.target.checked ? dataForm.name : dataForm.patientName,
                            contactRelationId: event.target.checked ? "" : dataForm.contactRelationId,
                          })
                        }
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: typography.fontSize.sm }}>
                        O atendimento é para a própria pessoa que entrou em contato
                      </Typography>
                    }
                  />

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <TextField
                      label="Nome do paciente"
                      value={dataForm.patientName}
                      onChange={(e) => setDataForm({ ...dataForm, patientName: e.target.value })}
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: NAME_MAX }}
                      disabled={dataForm.patientIsContact}
                      helperText={dataForm.patientIsContact ? "Mesmo nome do contato." : undefined}
                    />
                    <TextField
                      type="date"
                      label="Nascimento do paciente"
                      value={dataForm.patientBirthDate}
                      onChange={(e) => setDataForm({ ...dataForm, patientBirthDate: e.target.value })}
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  {!dataForm.patientIsContact && (
                    <TextField
                      select
                      label="Parentesco com o paciente"
                      value={dataForm.contactRelationId}
                      onChange={(e) =>
                        setDataForm({ ...dataForm, contactRelationId: e.target.value })
                      }
                      fullWidth
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                      helperText="Como quem entrou em contato se relaciona com o paciente."
                    >
                      <MenuItem value="">Não informado</MenuItem>
                      {parentRelations.map((relation) => (
                        <MenuItem key={relation.id} value={relation.id}>
                          {relation.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {/* Origem e satisfação: a Lidi pergunta isso na última etapa do roteiro e
                      preenche sozinha; aqui a recepção confere e corrige. */}
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <TextField
                      select
                      label="Onde encontrou a clínica"
                      value={dataForm.contactOrigin}
                      onChange={(e) =>
                        setDataForm({
                          ...dataForm,
                          contactOrigin: e.target.value,
                          // troca de origem invalida o detalhe da anterior
                          contactOriginNote: "",
                        })
                      }
                      sx={{ ...filterFieldSx, flex: "1 1 220px" }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Não informado</MenuItem>
                      {Object.entries(LEAD_CONTACT_ORIGINS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Nota do atendimento (0 a 10)"
                      value={dataForm.attendanceRating}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
                        if (digits === "" || Number(digits) <= 10) {
                          setDataForm({ ...dataForm, attendanceRating: digits });
                        }
                      }}
                      sx={{ ...filterFieldSx, flex: "0 1 200px" }}
                      size="small"
                      inputProps={{ maxLength: 2, inputMode: "numeric" }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  {LEAD_ORIGIN_DETAIL_LABEL[dataForm.contactOrigin] && (
                    <TextField
                      label={LEAD_ORIGIN_DETAIL_LABEL[dataForm.contactOrigin]}
                      value={dataForm.contactOriginNote}
                      onChange={(e) =>
                        setDataForm({ ...dataForm, contactOriginNote: e.target.value })
                      }
                      fullWidth
                      size="small"
                      sx={filterFieldSx}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 200 }}
                    />
                  )}

                  <TextField
                    label="Queixa / diagnóstico"
                    value={dataForm.diagnosisNote}
                    onChange={(e) => setDataForm({ ...dataForm, diagnosisNote: e.target.value })}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: NOTE_MAX }}
                  />
                  <TextField
                    label="Observações"
                    value={dataForm.notes}
                    onChange={(e) => setDataForm({ ...dataForm, notes: e.target.value })}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: NOTE_MAX }}
                  />
                  {detail.lostReason && (
                    <Box>
                      <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>
                        Motivo da perda
                      </Typography>
                      <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textPrimary }}>
                        {detail.lostReason}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>
                      Criado em {formatDateTime(detail.createdAt)}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={saving}
                      onClick={saveDataForm}
                      sx={{
                        backgroundColor: colors.primary,
                        "&:hover": { backgroundColor: colors.primaryHover },
                        textTransform: "none",
                      }}
                    >
                      Salvar alterações
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {drawerTab === 0 && (
              <Box sx={{ p: 1.5, borderTop: "1px solid #e9ecef", display: "flex", gap: 1, backgroundColor: "white" }}>
                <TextField
                  size="small"
                  placeholder={
                    detail.whatsapp || detail.phone
                      ? "Responder pelo WhatsApp (janela de 24h)"
                      : "Lead sem número de WhatsApp"
                  }
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  fullWidth
                  multiline
                  maxRows={3}
                  disabled={!detail.whatsapp && !detail.phone}
                  sx={filterFieldSx}
                  inputProps={{ maxLength: MESSAGE_MAX }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                />
                <Tooltip title="Enviar">
                  <span>
                    <IconButton
                      onClick={sendReply}
                      disabled={!reply.trim() || saving || (!detail.whatsapp && !detail.phone)}
                      sx={{
                        backgroundColor: colors.whatsapp,
                        color: "white",
                        "&:hover": { backgroundColor: colors.whatsappHover },
                        "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
                      }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Dialog>

      {/* Novo lead / edição */}
      <Dialog
        open={leadDialogOpen}
        onClose={() => setLeadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { position: "relative", overflow: "hidden", borderRadius: "12px", maxHeight: "85vh" } }}
      >
        <SavingOverlay open={saving} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: "1rem 1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography component="h3" sx={{ fontSize: "1.15rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {leadForm.id ? "Editar lead" : "Novo lead"}
          </Typography>
          <IconButton
            onClick={() => setLeadDialogOpen(false)}
            sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "1rem !important" }}>
          <TextField
            label="Nome do contato"
            value={leadForm.name}
            onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
            fullWidth
            size="small"
            sx={filterFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: NAME_MAX }}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="WhatsApp (com DDD)"
              placeholder="11999998888"
              value={leadForm.whatsapp}
              onChange={(e) => setLeadForm({ ...leadForm, whatsapp: e.target.value })}
              size="small"
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: PHONE_MAX }}
            />
            <TextField
              label="Telefone"
              value={leadForm.phone}
              onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
              size="small"
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: PHONE_MAX }}
            />
          </Box>
          <TextField
            label="Email"
            value={leadForm.email}
            onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
            fullWidth
            size="small"
            sx={filterFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: EMAIL_MAX }}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select
              label="Origem"
              value={leadForm.source}
              onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}
              size="small"
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
            >
              {Object.entries(LEAD_SOURCES).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Prioridade"
              value={leadForm.priority}
              onChange={(e) => setLeadForm({ ...leadForm, priority: e.target.value })}
              size="small"
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
            >
              {Object.entries(LEAD_PRIORITIES).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {!leadForm.id && (
            <TextField
              select
              label="Estágio inicial"
              value={leadForm.stageId}
              onChange={(e) => setLeadForm({ ...leadForm, stageId: e.target.value })}
              fullWidth
              size="small"
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
            >
              {board?.stages
                .filter((stage) => !stage.isWon && !stage.isLost)
                .map((stage) => (
                  <MenuItem key={stage.id} value={stage.id}>
                    {stage.name}
                  </MenuItem>
                ))}
            </TextField>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              label="Nome da criança"
              value={leadForm.patientName}
              onChange={(e) => setLeadForm({ ...leadForm, patientName: e.target.value })}
              size="small"
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: NAME_MAX }}
            />
            <TextField
              type="date"
              label="Nascimento da criança"
              value={leadForm.patientBirthDate}
              onChange={(e) => setLeadForm({ ...leadForm, patientBirthDate: e.target.value })}
              size="small"
              sx={filterFieldSx}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Queixa / diagnóstico"
            value={leadForm.diagnosisNote}
            onChange={(e) => setLeadForm({ ...leadForm, diagnosisNote: e.target.value })}
            fullWidth
            size="small"
            multiline
            minRows={2}
            sx={filterFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: NOTE_MAX }}
          />
          <TextField
            label="Observações"
            value={leadForm.notes}
            onChange={(e) => setLeadForm({ ...leadForm, notes: e.target.value })}
            fullWidth
            size="small"
            multiline
            minRows={2}
            sx={filterFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: NOTE_MAX }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLeadDialogOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={saveLead}
            disabled={saving}
            sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            {leadForm.id ? "Salvar" : "Criar lead"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Motivo da perda (mover para coluna de perda) */}
      <Dialog
        open={!!lostDialog}
        onClose={() => setLostDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { position: "relative", overflow: "hidden", borderRadius: "12px" } }}
      >
        <SavingOverlay open={saving} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: "1rem 1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography component="h3" sx={{ fontSize: "1.15rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Motivo da perda
          </Typography>
          <IconButton
            onClick={() => setLostDialog(null)}
            sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "1rem !important" }}>
          <TextField
            autoFocus
            label="Por que este lead foi perdido?"
            placeholder="Ex.: preço, distância, fechou com outra clínica"
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            sx={filterFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: LOST_REASON_MAX }}
            helperText="O motivo alimenta os relatórios de captação."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLostDialog(null)} sx={{ color: colors.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!lostReason.trim() || saving}
            onClick={async () => {
              if (!lostDialog) return;
              const { leadId, stageId } = lostDialog;
              setLostDialog(null);
              await executeMove(leadId, stageId, lostReason.trim());
            }}
            sx={{ backgroundColor: colors.error, "&:hover": { backgroundColor: "#c82333" } }}
          >
            Registrar perda
          </Button>
        </DialogActions>
      </Dialog>

      <OverdueLeadsModal
        entityId={entityId}
        onOpenLead={(leadId, pipelineId) => {
          openPipeline(pipelineId);
          void openLead(leadId);
        }}
      />

      {/* Cadastro do paciente a partir do lead. A busca por telefone INFORMA cadastros que já
          existem — não bloqueia: mesmo telefone costuma ser a mãe cadastrando o segundo filho. */}
      <Dialog
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: "1rem 1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: typography.fontWeight.semibold }}>
            Gerar cadastro de paciente
          </Typography>
          <IconButton onClick={() => setConvertOpen(false)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "1.25rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          {intakeLink ? (
            <>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textPrimary }}>
                Cadastro criado. Mande o link abaixo para a família completar os dados que faltam.
              </Typography>
              <Paper
                elevation={0}
                sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: "8px", p: 1 }}
              >
                <Typography
                  sx={{
                    fontSize: typography.fontSize.xs,
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    color: colors.textPrimary,
                  }}
                >
                  {intakeLink}
                </Typography>
              </Paper>
              <Button
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(intakeLink).then(
                    () => showToast("Link copiado.", "success"),
                    () => showToast("Não foi possível copiar.", "error"),
                  );
                }}
                sx={{ alignSelf: "flex-start", textTransform: "none", borderColor: colors.primary, color: colors.primary }}
              >
                Copiar link do formulário
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                Confira os dados abaixo. O cadastro do paciente exige nome, nascimento, telefone e
                email — o que a conversa já apurou vem preenchido. O lead passa a "ganho" e fica
                vinculado ao paciente.
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <TextField
                  label="Nome do paciente"
                  value={convertForm.patientName}
                  onChange={(e) => setConvertForm({ ...convertForm, patientName: e.target.value })}
                  inputProps={{ maxLength: 200 }}
                  size="small"
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Data de nascimento"
                  type="date"
                  value={convertForm.birthDate}
                  onChange={(e) => setConvertForm({ ...convertForm, birthDate: e.target.value })}
                  size="small"
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <PhoneWithCountryInput
                  label="Telefone"
                  value={convertForm.phone}
                  onChange={(phone) => setConvertForm({ ...convertForm, phone })}
                  fieldSx={filterFieldSx}
                  labelProps={{ shrink: true }}
                />
                <TextField
                  label="Email"
                  value={convertForm.email}
                  onChange={(e) => setConvertForm({ ...convertForm, email: e.target.value })}
                  inputProps={{ maxLength: 150 }}
                  size="small"
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  helperText={detail?.email ? undefined : "A conversa não trouxe o email — peça na conversa."}
                />
                {!detail?.patientIsContact && (
                  <TextField
                    label="Responsável"
                    value={convertForm.responsibleName}
                    onChange={(e) => setConvertForm({ ...convertForm, responsibleName: e.target.value })}
                    inputProps={{ maxLength: 200 }}
                    size="small"
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </Box>

              {matchesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: colors.primary }} />
                </Box>
              ) : matches.length > 0 ? (
                <Paper
                  elevation={0}
                  sx={{ backgroundColor: "#fff9db", border: "1px solid #ffe066", borderRadius: "8px", p: 1.25 }}
                >
                  <Typography
                    sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, mb: 0.75 }}
                  >
                    Já existe cadastro com este telefone
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {matches.map((match) => (
                      <Box
                        key={match.patientId}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1,
                          backgroundColor: "white",
                          borderRadius: "6px",
                          p: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
                          <Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
                            {match.patientName}
                          </Typography>
                          <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                            {match.birthDate ? `Nasc. ${formatDate(match.birthDate)} · ` : ""}
                            {match.phone || "-"} ({match.matchedOn})
                            {match.responsibleName ? ` · Resp.: ${match.responsibleName}` : ""}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => convert(match.patientId)}
                          sx={{ textTransform: "none", borderColor: colors.primary, color: colors.primary, flexShrink: 0 }}
                        >
                          Vincular a este
                        </Button>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, mt: 1 }}>
                    Mesmo telefone costuma ser a mesma família — outro filho, por exemplo. Vincule
                    se for a mesma pessoa; crie um cadastro novo se for outra.
                  </Typography>
                </Paper>
              ) : (
                <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                  Nenhum cadastro existente com este telefone.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setConvertOpen(false)} sx={{ color: colors.textSecondary }}>
            {intakeLink ? "Fechar" : "Cancelar"}
          </Button>
          {!intakeLink && (
            <Button
              variant="contained"
              onClick={() => convert(null)}
              sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
            >
              Criar cadastro novo
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Transferência entre pipelines: o gesto que falta para a RN-627c — quem entrou por um
          funil e deve ser tocado por outro time muda de lugar por decisão de gente, não sozinho. */}
      <Dialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: "1rem 1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: typography.fontWeight.semibold }}>
            Transferir para outro pipeline
          </Typography>
          <IconButton onClick={() => setTransferOpen(false)} sx={{ color: colors.white, padding: "0.25rem" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "1.25rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            size="small"
            label="Pipeline de destino"
            value={transferPipelineId}
            onChange={(e) => chooseTransferPipeline(e.target.value)}
            fullWidth
            sx={filterFieldSx}
          >
            {pipelines
              .filter((p) => p.active && p.id !== detail?.pipelineId)
              .map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                  {p.agentName ? ` · ${p.agentName}` : " · atendimento humano"}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Estágio de destino"
            value={transferStageId}
            onChange={(e) => setTransferStageId(e.target.value)}
            disabled={!transferPipelineId}
            fullWidth
            sx={filterFieldSx}
          >
            {(pipelines.find((p) => p.id === transferPipelineId)?.stages || [])
              .filter((stage) => stage.active && !stage.isWon && !stage.isLost)
              .map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Responsável no pipeline de destino"
            value={transferUserId}
            onChange={(e) => setTransferUserId(e.target.value)}
            disabled={!transferPipelineId || transferLoading}
            helperText={
              transferLoading
                ? "Carregando quem pode receber o lead nesse pipeline..."
                : transferPipelineId && transferUsers.length === 0
                  ? "Nenhum usuário pode receber leads nesse pipeline — ajuste os responsáveis dele na configuração."
                  : "O lead precisa de dono no destino: sem responsável definido é o que mais deixa lead parado."
            }
            fullWidth
            sx={filterFieldSx}
          >
            {transferUsers.map((user) => (
              <MenuItem key={user.userId} value={user.userId}>
                {user.name}
              </MenuItem>
            ))}
          </TextField>

          <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
            O atendimento por IA é do pipeline: ao transferir, quem responde passa a ser o agente do
            destino — ou ninguém, se ele for de atendimento humano. O prazo do estágio recomeça.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setTransferOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submitTransfer}
            disabled={!transferPipelineId || !transferStageId || !transferUserId}
            sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            Transferir
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={confirmAssumeOpen}
        title="Assumir este atendimento?"
        message={
          "Ao responder, você passa a ser o responsável por este lead. " +
          "O agente de IA para de responder esta conversa e não volta — transferir para outra " +
          "pessoa continua possível."
        }
        confirmButtonText="Assumir e enviar"
        cancelButtonText="Cancelar"
        type="warning"
        isProcessing={saving}
        onConfirm={assumeAndSend}
        onCancel={() => setConfirmAssumeOpen(false)}
      />

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Excluir lead"
        message={`Excluir o lead "${confirmDelete?.name}"? Ele sai do funil, mas o histórico é preservado.`}
        type="danger"
        onConfirm={deleteLead}
        onCancel={() => setConfirmDelete(null)}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <FooterInternal />
    </Box>
  );
};

export default CrmFunnel;
